import { useMemo } from "react";
import DataGrid, {
    Column,
    Export,
    GroupItem,
    GroupPanel,
    KeyboardNavigation,
    Paging,
    Pager,
    SearchPanel,
    Summary,
    TotalItem
} from 'devextreme-react/data-grid';
import { allowedPageSizes, pageFormats } from "../../components/GlobalDataGridConfigurations";
import CustomStore from 'devextreme/data/custom_store';
import ExportGrid from '../../components/ExportGrid';

export default function DataGridSpecificCostEstimateSummary(
    { 
        error,
        groupingEnabled,
        fiscalYear,
        isLoading,
        showDataGrid,
        summaryCosts
    }
){
    const handleExport = (e) => {
        ExportGrid(e, `Specific_Cost_Estimate_Summary_${fiscalYear}`);
    };

    const summaryData = useMemo(() => new CustomStore({
        key: 'ID',
        load: async () => {
            if (summaryCosts !== null) {
                const combinedSecurityDetailsRecords = new Map();
                summaryCosts.SecurityCostDetails.forEach(securityDetail => {
                    const key = `${securityDetail.Region}-${securityDetail.BuildingSequenceNo}-${securityDetail.FeeTypeId}`;
                    const psoCost = securityDetail.SecurityCostType === "PSO" ? securityDetail.Charges : null;
                    const tcmCost = securityDetail.SecurityCostType === "TCM" ? securityDetail.Charges : null;
                    const amCapCost = securityDetail.SecurityCostType === "AMORCAP" ? securityDetail.Charges : null;
                    const feeType = securityDetail.FeeTypeId === 2 ? "Building" : (securityDetail.FeeTypeId === 3 ? "Agency": null);

                    if (combinedSecurityDetailsRecords.has(key)) {
                        const existingSecurityDetailRecord = combinedSecurityDetailsRecords.get(key);
                        combinedSecurityDetailsRecords.set(key, {
                            ...existingSecurityDetailRecord,
                            PsoCost: existingSecurityDetailRecord.PsoCost + psoCost,
                            TcmCost: existingSecurityDetailRecord.TcmCost + tcmCost,
                            AmCapCost: existingSecurityDetailRecord.AmCapCost + amCapCost,
                            TotalFyCost: (psoCost+ tcmCost + amCapCost) + existingSecurityDetailRecord.TotalFyCost
                        })

                    } else {
                        combinedSecurityDetailsRecords.set(key, {
                            FeeType: feeType,
                            ID: securityDetail.SecurityCostId,
                            FiscalYear: securityDetail.FiscalYear,
                            Region: securityDetail.Region,
                            BuildingNumber: securityDetail.BuildingSequenceNo,
                            CustomerName: securityDetail.CustomerName,
                            CustomerNumber: securityDetail.CustomerNumber,
                            PsoCost: psoCost,
                            TcmCost: tcmCost,
                            AmCapCost: amCapCost,
                            TotalFyCost: (psoCost+ tcmCost + amCapCost) 
                        })
                    }
                });

                return Array.from(combinedSecurityDetailsRecords.values());                   
            }
            return [];
        }
    }), [summaryCosts, fiscalYear]);

    const combinedOnCellPrepared = (e) => {
        if (e.rowType === "group" && e.column) {
            const isRegionGroup = e.column.dataField === "Region";
            const isBuildingGroup = e.column.dataField === "BuildingNumber";
            const isFeeTypeGroup = e.column.dataField === "FeeType";
    
            let groupName = "";
            if (isRegionGroup) groupName = `Region ${e.data?.key}`;
            else if (isBuildingGroup) groupName = `Building Number ${e.data?.key}`;
            else if (isFeeTypeGroup) groupName = `Fee Type ${e.data?.key}`;
            else groupName = `${e.column.caption}: ${e.data?.key}`;
    
            const isExpanded = e.row.isExpanded;
            const ariaLabel = isExpanded ? `Collapse ${groupName}` : `Expand ${groupName}`;
            const ariaExpanded = isExpanded ? "true" : "false";
    
            e.cellElement.setAttribute("aria-label", ariaLabel);
            e.cellElement.setAttribute("aria-expanded", ariaExpanded);
        }
    };    

    return (
        showDataGrid &&
        <DataGrid
            columnAutoWidth={true}
            dataSource={summaryData}
            onCellPrepared={combinedOnCellPrepared}
            onExporting={e => handleExport(e)}
            noDataText={isLoading ? "" : error}
            rowAlternationEnabled={true}
            showBorders={true}
            showRowLines={true}
            style={{ color: "#06152B" }}
        >
            <KeyboardNavigation enabled={true} />
            <Paging defaultPageSize={10} />
            <Pager
                allowedPageSizes={allowedPageSizes}
                showPageSizeSelector={true}
            />
            <SearchPanel
                highlightCaseSensitive={true}
                visible={true}
            />
            <GroupPanel visible={true} />
            <Column 
                caption="Fee Type" 
                dataField="FeeType" 
                groupIndex={groupingEnabled ? null : 2}
            />
            <Column 
                caption="Region" 
                dataField="Region" 
                groupIndex={groupingEnabled ? null : 0} 
            />
            <Column 
                caption="Building Number" 
                dataField="BuildingNumber" 
                groupIndex={groupingEnabled ? null : 0}
            />
            <Column 
                caption="PSO FY Cost Estimate" 
                dataField="PsoCost" 
                format="currency" 
            />
            <Column 
                caption="TCM FY Cost Estimate" 
                dataField="TcmCost" 
                format="currency" 
            />
            <Column 
                caption="AM CAP FY Cost Estimate" 
                dataField="AmCapCost" 
                format="currency" 
            />
            <Column 
                caption="Total FY Cost Estimate" 
                dataField="TotalFyCost" 
                format="currency" 
            />
            <Summary>
                <GroupItem
                    alignByColumn={true}
                    column="PsoCost"
                    displayFormat="PSO FY Cost Estimate: {0}" 
                    summaryType="sum"
                    valueFormat="currency"
                />
                <GroupItem
                    alignByColumn={true}
                    column="TcmCost"
                    displayFormat="TCM FY Cost Estimate: {0}" 
                    summaryType="sum"
                    valueFormat="currency"
                />
                <GroupItem
                    alignByColumn={true}
                    column="AmCapCost"
                    displayFormat="AM CAP FY Cost Estimate: {0}" 
                    summaryType="sum"
                    valueFormat="currency"
                />
                <GroupItem
                    alignByColumn={true}
                    column="TotalFyCost"
                    displayFormat="Total FY Cost Estimate: {0}" 
                    summaryType="sum"
                    valueFormat="currency"
                />
                <TotalItem
                    alignByColumn={true} 
                    column="PsoCost"
                    displayFormat="PSO Grand Total: {0}"
                    summaryType="sum"
                    valueFormat="currency"
                />
                <TotalItem
                    alignByColumn={true} 
                    column="TcmCost"
                    displayFormat="TCM Grand Total: {0}"
                    summaryType="sum"
                    valueFormat="currency"
                />
                <TotalItem
                    alignByColumn={true} 
                    column="AmCapCost"
                    displayFormat="AM CAP Grand Total: {0}"
                    summaryType="sum"
                    valueFormat="currency"
                />
                <TotalItem
                    alignByColumn={true} 
                    column="TotalFyCost"
                    displayFormat="Grand Total: {0}"
                    summaryType="sum"
                    valueFormat="currency"
                />
            </Summary> 
            <Export
                enabled={groupingEnabled}
                formats={pageFormats}
            />
        </DataGrid>
    );
};